/**
 * SUB-2.2 · FR3, FR5, FR68. The reading and the wording of a parent account's
 * sub-account allowance.
 *
 * All of this is pure and lives here rather than in the settings card, because the
 * three things it decides are the three things the ACs are stated in terms of:
 *
 *   FR5   the exact display string, "4 of 10 sub-accounts" / "4 sub-accounts (unlimited)"
 *   FR3   a numeric cap and explicit unlimited are DIFFERENT choices, not one field
 *         where empty happens to mean infinity
 *   FR68  an allowance below current usage is legal, not a validation error
 *
 * The counting is deliberately NOT done here. `sub_account_usage()` counts the
 * children, applies FR67 (archived sub-accounts do not consume the allowance) and
 * authorizes the tenant id it was handed. Recomputing any of that in the browser
 * would be a second definition of the same fact, which is the ISO2 mistake the
 * subtree resolver exists to avoid. This module only reads what that RPC returns,
 * by way of `GET /tenants/{id}/sub-accounts`, and turns it into words.
 *
 * ISO12 is handled one layer up: the RPC answers `{"status":"invalid"}` for both a
 * nonexistent account and one the caller may not ask about, and the endpoint maps
 * that single answer to a single 404. Nothing here tries to tell them apart.
 */

/** The shape `public.sub_account_usage(uuid)` returns on success. */
export interface SubAccountUsage {
  /** Whether the account may hold sub-accounts at all (FR1). */
  enabled: boolean;
  /** Non-archived children today (FR67). */
  used: number;
  /** The cap, or null for unlimited (FR3). */
  allowance: number | null;
  unlimited: boolean;
  /** True when `used >= allowance`. Honest when usage EXCEEDS a lowered cap (FR68). */
  atLimit: boolean;
}

function asFiniteInt(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.trunc(value);
}

/**
 * Reads the API's reading of the RPC.
 *
 * Returns null for a payload this cannot make sense of, which is corruption
 * rather than a policy answer: an account the caller may not ask about never
 * reaches here, because the endpoint has already turned it into a 404.
 *
 * `at_limit` from the payload is deliberately ignored and recomputed. It is a
 * derived value, and a stale or hand-built response must not be able to claim
 * room in the allowance that the numbers do not support.
 */
export function parseSubAccountUsage(raw: unknown): SubAccountUsage | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;

  const used = asFiniteInt(row.used);
  if (used === null) return null;

  // `allowance` is genuinely nullable, so absent and null both mean unlimited,
  // and a non-numeric value is corruption rather than unlimited.
  const allowance =
    row.allowance === null || row.allowance === undefined
      ? null
      : asFiniteInt(row.allowance);
  if (row.allowance !== null && row.allowance !== undefined && allowance === null) {
    return null;
  }

  return {
    enabled: row.enabled === true,
    used,
    allowance,
    unlimited: allowance === null,
    atLimit: allowance !== null && used >= allowance,
  };
}

/** "sub-account" / "sub-accounts", pluralized on the number that precedes it. */
function noun(count: number): string {
  return count === 1 ? "sub-account" : "sub-accounts";
}

/**
 * FR5, verbatim: "4 of 10 sub-accounts" or "4 sub-accounts (unlimited)".
 *
 * The noun agrees with the number immediately before it, which is the allowance in
 * the capped form and the usage in the unlimited form. That is why "1 of 1
 * sub-account" and "4 of 1 sub-account" both read correctly while "1 of 10
 * sub-accounts" does too.
 *
 * Nothing about being at or over the limit belongs in this string. FR5 specifies
 * what it says; `subAccountUsageNote` carries the rest.
 */
export function formatSubAccountUsage(usage: SubAccountUsage): string {
  if (usage.unlimited) {
    return `${usage.used} ${noun(usage.used)} (unlimited)`;
  }
  return `${usage.used} of ${usage.allowance} ${noun(usage.allowance as number)}`;
}

/**
 * The sentence that goes under the FR5 line, or null when there is nothing to say.
 *
 * FR68 is the reason the "over" case is worded as a statement rather than a
 * warning: a superadmin lowering the allowance below current usage has done
 * something legal and deliberate, and the existing sub-accounts are untouched.
 * Only new creation is blocked (FR4).
 */
export function subAccountUsageNote(usage: SubAccountUsage): string | null {
  if (usage.unlimited || usage.allowance === null) return null;
  if (usage.used > usage.allowance) {
    return `Over the allowance. Existing ${noun(usage.used)} are unaffected; no new ones can be created until the allowance is raised.`;
  }
  if (usage.atLimit) {
    return "At the allowance. No new sub-accounts can be created until it is raised.";
  }
  return null;
}

/**
 * FR3. A numeric cap and unlimited are two choices the superadmin makes explicitly.
 *
 * The database says the same thing a different way: `sub_accounts_allowance` is
 * NULL for unlimited rather than a sentinel, precisely because 0 is a real value
 * under FR68 (freeze new creation, leave existing sub-accounts alone). A single
 * input where "blank" means unlimited would collapse "no more" and "no limit" into
 * one gesture, so the mode is carried separately all the way to the RPC argument.
 */
export type AllowanceMode = "limited" | "unlimited";

export interface AllowanceParse {
  /** The value to send as `p_allowance`: a number, or null for unlimited. */
  value: number | null;
  /** Set when the input cannot be sent at all. */
  error: string | null;
}

/**
 * Validates the allowance field against what the column will actually accept.
 *
 * The bound is the live CHECK constraint `tenants_allowance_non_negative`
 * (`sub_accounts_allowance IS NULL OR sub_accounts_allowance >= 0`), read from
 * pg_constraint rather than assumed. Zero is valid and meaningful; negative and
 * fractional are not.
 */
export function parseAllowanceInput(
  mode: AllowanceMode,
  input: string,
): AllowanceParse {
  if (mode === "unlimited") return { value: null, error: null };

  const trimmed = input.trim();
  if (trimmed === "") {
    return { value: null, error: "Enter a number, or choose Unlimited." };
  }
  if (!/^\d+$/.test(trimmed)) {
    return {
      value: null,
      error: "The allowance must be a whole number of 0 or more.",
    };
  }

  const value = Number(trimmed);
  if (!Number.isSafeInteger(value)) {
    return { value: null, error: "That number is too large." };
  }
  return { value, error: null };
}

/**
 * FR68 again, at the point of editing rather than the point of reading: what the
 * superadmin is about to do to the accounts that already exist.
 *
 * Returned as information, never as a blocker. Refusing this save would be the
 * product inventing a rule the PRD explicitly rejects.
 */
export function allowanceChangeNote(
  pending: number | null,
  used: number,
): string | null {
  if (pending === null) return null;
  if (pending >= used) return null;
  if (pending === 0) {
    return `Sets the allowance to zero. The ${used} existing ${noun(used)} stay exactly as they are; only new ones are blocked.`;
  }
  return `Below current usage. The ${used} existing ${noun(used)} stay exactly as they are; only new ones are blocked.`;
}

/**
 * The form state an existing reading implies, so the card opens showing what is
 * configured rather than a default that would silently change it on save.
 *
 * An account that has never been configured reads as `unlimited` from the RPC
 * (allowance NULL), which is correct for display but a poor default to hand a
 * superadmin who is turning the capability on for the first time: FR3 wants a
 * deliberate choice. `enabled === false` is therefore the one case that opens on
 * "limited" with an empty box, which cannot be saved without typing a number or
 * picking Unlimited on purpose.
 */
export function allowanceFormState(usage: SubAccountUsage | null): {
  mode: AllowanceMode;
  input: string;
} {
  if (!usage || !usage.enabled) return { mode: "limited", input: "" };
  if (usage.allowance === null) return { mode: "unlimited", input: "" };
  return { mode: "limited", input: String(usage.allowance) };
}
